import { discriminatedUnion, literal, nativeEnum, number, object, preprocess } from 'zod';

import { IS_POSITIVE, IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { Award_Per_Counting_Strategy } from '@occc/graphql';
import { ProductGroupsSchema } from '@occc/validation';

import { BasePromotionSchema } from './base-promotion.schema';

import { ProductDiscountAwardType, PromotionType } from '../types';

const requiredPurchaseThreshold = number({ required_error: IS_REQUIRED }).gt(0, {
  message: IS_POSITIVE,
});

const ProductDiscountBaseSchema = object({
  eligibleProductGroups: ProductGroupsSchema,
});

const countProductsBySchema = nativeEnum(
  {
    IndividualPurchase: Award_Per_Counting_Strategy.IndividualPurchase,
    SellingUnitPurchase: Award_Per_Counting_Strategy.SellingUnitPurchase,
  } as const,
  { required_error: IS_REQUIRED },
);

export const ProductDiscountEarningMechanismSchema = discriminatedUnion('awardType', [
  ProductDiscountBaseSchema.extend({
    awardType: literal(ProductDiscountAwardType.Free),
    purchaseThreshold: requiredPurchaseThreshold,
    moneyAmount: number().optional(),
  }),
  ProductDiscountBaseSchema.extend({
    awardType: literal(ProductDiscountAwardType.Discount),
    purchaseThreshold: number().gt(0, { message: IS_POSITIVE }).optional(),
    moneyAmount: number({ required_error: IS_REQUIRED }).positive(IS_POSITIVE),
    countProductsBy: countProductsBySchema.optional(),
  }),
  ProductDiscountBaseSchema.extend({
    awardType: literal(ProductDiscountAwardType.PriceOverride),
    purchaseThreshold: requiredPurchaseThreshold,
    moneyAmount: number({ required_error: IS_REQUIRED }).gt(0, { message: IS_POSITIVE }),
  }),
]);

export const ProductDiscountFormSchema = BasePromotionSchema.extend({
  offerType: literal(PromotionType.ProductDiscount),
  productDiscount: preprocess((val) => val ?? {}, ProductDiscountEarningMechanismSchema),
}).superRefine((data, ctx) => {
  const { productDiscount } = data;

  if (productDiscount.awardType !== ProductDiscountAwardType.Discount) {
    return;
  }

  if (productDiscount.purchaseThreshold != null && productDiscount.countProductsBy == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['productDiscount', 'countProductsBy'],
      message: IS_REQUIRED,
    });
  }
});
